import { ChromeData, UserInfo } from '@amzn/aac-chrome-internal-api';
import { store } from 'src/app/store';
import { getChromeData } from 'src/commons/apis';
import { logger } from 'src/commons/utils/logger';
import {
  HeaderState,
  initHeaderState,
  updateUserInfo,
} from 'src/features/header/store/slice';

const LOGIN_CONTEXT_KEY = 'loginContext';
const LOGIN_CONTEXT_EVENT = 'sessionStorage.loginContext';

const toHeaderState = (chromeData: ChromeData): HeaderState => ({
  ...chromeData.chromeHeaderData,
  ...chromeData.chromeSharedData,
});

/**
 * ChromeData can come back without the user's name and email. The host page
 * publishes them separately as sessionStorage.loginContext, possibly after
 * Chrome has rendered, so fill them in now or when that event fires.
 */
const mergeLoginContext = (userInfo: UserInfo) => {
  // Returns whether it merged. A missing or malformed loginContext is logged
  // and skipped: it must not throw out of an event listener, or turn a
  // successful ChromeData load into a reported failure.
  const merge = (): boolean => {
    try {
      const loginContext = JSON.parse(
        sessionStorage.getItem(LOGIN_CONTEXT_KEY)!,
      );
      store.dispatch(
        updateUserInfo({
          ...userInfo,
          customerInfo: {
            fullName: loginContext.userName.value,
            primaryEmail: loginContext.userEmail.value,
          },
        }),
      );
      return true;
    } catch (e: unknown) {
      logger.error('failed to merge loginContext:', e as Error);
      return false;
    }
  };

  if (sessionStorage.getItem(LOGIN_CONTEXT_KEY)) {
    merge();
    return;
  }
  // Stop listening after the first successful merge: a later event would
  // re-dispatch this call's userInfo snapshot. A failed merge keeps listening.
  const onLoginContext = () => {
    if (merge()) {
      window.removeEventListener(LOGIN_CONTEXT_EVENT, onLoginContext);
    }
  };
  window.addEventListener(LOGIN_CONTEXT_EVENT, onLoginContext);
};

/**
 * Seeds the header store from ChromeData, for pages that render the header
 * without server-supplied config. Resolves to whether the load succeeded; it
 * never rejects, so callers can fire it alongside the first render.
 */
export const loadHeaderStateFromChromeData = async (): Promise<boolean> => {
  try {
    const headerState = toHeaderState(await getChromeData());
    store.dispatch(initHeaderState(headerState));
    if (!headerState.userInfo.customerInfo.primaryEmail) {
      mergeLoginContext(headerState.userInfo);
    }
    return true;
  } catch (e: unknown) {
    // getChromeData rejects with an Error subclass (ForbiddenError etc.).
    logger.error('failed to fetch chromeData:', e as Error);
    return false;
  }
};
